import React from 'react';
import { connect } from 'react-redux';
import Cookies from 'universal-cookie';
import * as Onfido from 'onfido-sdk-ui';
import * as Sentry from '@sentry/react';

import Loader from '../components/loader';
import ToastHandler from '../components/toasthandler';
import { initiateChecks, generateSDKToken, checkStatus } from '../actions';

const cookie = new Cookies();

const stageByStatus = {
  in_progress: 2,
  waiting_for_approval: 2,
  failed: 3,
  reupload: 4,
  successful: 5
};
class OnfidoSdk extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      stage: 1,
      onfidoStage: 1,
      loading: false,
      onfidoInstance: null,
      userConsent: false,
      showCheckFailed: false,
    };

    this.containerId = 'onfido-mount';
    this.initOnfido = this.initOnfido.bind(this);
    this.handleVerificationCheck = this.handleVerificationCheck.bind(this);
    this.getToken = this.getToken.bind(this);
    this.handleAccept = this.handleAccept.bind(this);
    this.handleNotAccept = this.handleNotAccept.bind(this);
    this.toggleFaq = this.toggleFaq.bind(this);
  }

  async componentDidMount() {
    const { dispatch } = this.props;
    const { payload, type } = await dispatch(checkStatus());
    let stage = 1;

    /*
      * * * * * * * * * * *
      -> status == null
        -> stage = 1
      -> status == in_progress
        -> stage = 2
      -> status == failed
        -> stage = 3
      -> status == reupload
        -> stage = 4
      -> status == successful
        -> stage = 5
      -> status === waiting_for_approval
        -> stage = 2
      * * * * * * * * * * *
    */

    if (type === 'REQUEST_VERIFICATIONS_STATUS_SUCCESS') {
      if (payload.status) {
        if (stageByStatus[payload.status]) {
          stage = stageByStatus[payload.status];
        }
      }
    }

    if (stage === 5) {
      this.props.history.push('/');
      return;
    }

    this.addOnfidoUserAnalyticsEventListener();
    this.setState((prev) => ({
      ...prev, stage,
    }));
  }

  componentDidUpdate(_, prevState) {
    const { stage, onfidoInstance, onfidoStage } = this.state;
    const { user } = this.props;
    const isUserFromUk = user && user.me && user.me.countryHandle && user.me.countryHandle === 'GB';
    // if (
    //   (stage === 1 || stage === 4)
    //   && prevState.onfidoStage !== onfidoStage
    //   && onfidoStage === 2
    //   && isUserFromUk
    // ) {
    //   onfidoInstance.tearDown();
    //   this.initOnfido('POA');
    // }
  }

  componentWillUnmount() {
    const { onfidoInstance } = this.state;

    if (onfidoInstance) {
      localStorage.removeItem('onfido_token');
      return onfidoInstance.tearDown();
    }
  }

  addOnfidoUserAnalyticsEventListener() {
    // Just to be sure, it will remove all woopra related things so that woopra analytics api request won't hit.
    window.addEventListener('userAnalyticsEvent', (event) => {
      const sdkWoopra = cookie.get('onfido-js-sdk-woopra');
      const wooTracker = cookie.get('wooTracker');
      if (sdkWoopra && wooTracker) {
        const domain = window.location.hostname;
        cookie.remove('wooTracker', { path: '/', domain: '.' + domain });
        cookie.remove('onfido-js-sdk-woopra', { path: '/', domain: '.' + domain });
      }
    });
  }

  getToken = async () => {
    const { dispatch } = this.props;
    const skdResult = await dispatch(generateSDKToken());

    if (skdResult.type === 'REQUEST_SDK_TOKEN_GENERATION_SUCCESS') {
      const { token } = skdResult.payload.data;
      this.setState((prev) => ({
        ...prev,
        token,
      }));
      return token;
    }

    if (skdResult.type === 'REQUEST_SDK_TOKEN_GENERATION_FAILED') {
      this.setState((prev) => ({
        ...prev,
        errors: (skdResult.errors && skdResult.errors.response.data) || ['Error processing request']
      }));
    }
  }

  handleAccept() {
    const { user } = this.props;
    const { status } = this.props.verification;
    const isUserFromUk = user && user.me && user.me.countryHandle && user.me.countryHandle === 'GB';

    if (status === null || status === 'reupload') {
      this.setState((prev) => ({
        ...prev,
        userConsent: true
      }));

      if (isUserFromUk) {
        this.initOnfido('GB');
      } else {
        this.initOnfido('INIT');
      }
    }
  }

  handleNotAccept() {
    window.location.href = '/';
  }

  async handleVerificationCheck(proofOfAddressCheck = false) {
    const { dispatch } = this.props;
    const { onfidoInstance } = this.state;
    const { user } = this.props;
    const onfidoToken = localStorage.getItem('onfido_token');
    const isUserFromUk = user && user.me && user.me.countryHandle && user.me.countryHandle === 'GB';

    let referenceId;
    if (user.me.referenceId) {
      referenceId = user.me.referenceId;
    }

    // When user is from UK and have uploaded the documents.
    // In onfidoStage == 2 user will be asked to upload proof of address related document.
    if (isUserFromUk && !proofOfAddressCheck) {
      return this.setState((prev) => ({
        ...prev,
        onfidoStage: 2,
      }));
    }

    try {
      const response = await dispatch(initiateChecks());
      // Just in case check failed, show the failed message
      if (response.type === 'REQUEST_CREATE_CHECK_FAILED') {
        const steps = !proofOfAddressCheck ? ['welcome', 'face', 'document'] : ['poa'];

        this.setState((prev) => ({
          ...prev,
          showCheckFailed: true
        }));
        // used to set the steps dynamically and change the complete stage message.
        onfidoInstance.setOptions({
          token: onfidoToken,
          containerId: this.containerId,
          steps: [
            ...steps,
            {
              type: 'complete',
              options: {
                message: 'There is an error during processing your verification',
                submessage: `Please try again, later`
              }
            }
          ]
        });
      }
    } catch (error) {
      Sentry.captureException(error, scope => {
        if (referenceId) {
          scope.setContext("referenceId", {id: referenceId});
        }
        scope.setContext("errorOrigin", {origin: "handleVerificationCheck catch"});
      });
    }
  }

  getVerificationStatusContent() {
    const { stage } = this.state;

    if (stage === 3) {
      return (
        <div
          className="onfido-kyc-modal"
          style={
            {
              height: '25.5em',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }
          }
        >
          <div className="modal-body">
            <h2>Verification Failed</h2>
            <p>
              Sadly we&apos;e been unable to verify your identity. As a result you won&apos;t be able to use Vaultoro.
            </p>
            <p>
              If you wish to discuss this further, please contact the support team.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        className="onfido-kyc-modal"
        style={
          {
            height: '25.5em',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }
        }
      >
        <div className="modal-body">
          <h2>Verification In Progress</h2>
          <p>
            Your documents are currently being checked. This process can take up to 3 days although it&apos;s usually much faster.
          </p>
          <p>
            You will be emailed when you have been accepted or if we require more information.
          </p>
        </div>
      </div>
    );
  }

  getOnfidoSteps = (type) => {
    let steps = [];

    switch (type) {
      case 'GB': {
        steps = [
          {
            type: 'welcome',
            options: {
              title: 'Verify your Vaultoro Account',
              descriptions: [
                'To trade with Vaultoro we will need to verify your identity.',
                'It will take a couple of minutes.',
                'Please ensure the documents provided are in Roman characters.'
              ],
            },
          },
          {
            type: 'poa',
            options: {
              documentTypes: {
                bank_building_society_statement: false,
                utility_bill: true,
                government_letter: true, // non-GBR only
                council_tax: true, // GBR only
                benefit_letters: true, // GBR only
              }
            }
          },
          'face',
          {
            type: 'document',
            options: {
              forceCrossDevice: false,
              // When set to true, desktop users will be forced to use their mobile devices to capture the document image.
              // They will be able to do so via the built-in SMS feature.
              // Use this option if you want to prevent file upload from desktop.
              documentTypes: {
                driving_licence: true,
                national_identity_card: true,
                passport: true,
                residence_permit: false
              },
            },
          },
          {
            type: 'complete',
            options: {
              message: 'You will receive an email about your verification',
              submessage: `If your verification fails, we'll let you know via email`
            }
          }
        ];
        break;
      }
      case 'INIT': {
        steps = [
          {
            type: 'welcome',
            options: {
              title: 'Verify your Vaultoro Account',
              descriptions: [
                'To trade with Vaultoro we will need to verify your identity.',
                'It will take a couple of minutes.',
                'Please ensure the documents provided are in Roman characters.'
              ],
            },
          },
          'face',
          {
            type: 'document',
            options: {
              forceCrossDevice: false,
              // When set to true, desktop users will be forced to use their mobile devices to capture the document image.
              // They will be able to do so via the built-in SMS feature.
              // Use this option if you want to prevent file upload from desktop.
              documentTypes: {
                driving_licence: true,
                national_identity_card: true,
                passport: true,
                residence_permit: false
              },
            },
          },
          {
            type: 'complete',
            options: {
              message: 'You will receive an email about your verification',
              submessage: `If your verification fails, we'll let you know via email`
            }
          }
        ];
        break;
      }
      default:
        steps = [
          'welcome',
          'face',
          'document',
          'complete'
        ];
      break;
    }
    return steps;
  }

  initOnfido = async (type) => {
    const { user } = this.props;
    let referenceId;
    if (user.me.referenceId) {
      referenceId = user.me.referenceId;
    }

    try {
      this.setState({
        loading: true
      });

      const token = await this.getToken();

      const steps = this.getOnfidoSteps(type);

      if (!token) {
        this.setState((prev) => ({
          ...prev,
          loading: false,
          userConsent: false,
        }));
        return;
      }

      if (localStorage.getItem('onfido_token')) {
        localStorage.removeItem('onfido_token');
      }

      localStorage.setItem('onfido_token', token);

      const instance = Onfido.init({
        token,
        containerId: this.containerId,
        onComplete: (data) => {
          this.handleVerificationCheck(type === 'GB');
        },
        onError: function(error) {
          let useError = error;
          if (error && error.message) {
            useError = error.message;
          }
          Sentry.captureException(useError, scope => {
            if (referenceId) {
              scope.setContext("referenceId", {id: referenceId});
            }
            scope.setContext("errorOrigin", {origin: "Onfido onError"});
          });
        },
        onUserExit: function(exitCode) {
          return exitCode;
        },
        steps,
      });

      if (!instance) {
        return;
      }

      this.setState({
        onfidoInstance: instance,
        loading: false
      });
    } catch (err) {
      let useErr = err;
      if (err && err.message) {
        useErr = err.message;
      }
      Sentry.captureException(useErr, scope => {
        if (referenceId) {
          scope.setContext("referenceId", {id: referenceId});
        }
        scope.setContext("errorOrigin", {origin: "initOnfido catch"});
      });
    }
  }

  toggleFaq(id) {
    const faq = document.getElementById(id);
    if (faq.classList.contains('open')) {
      faq.classList.remove('open');
    } else {
      faq.classList.add('open');
    }
  }

  render() {
    const { loading, userConsent, stage, errors, showCheckFailed } = this.state;
    const { verification } = this.props;

    if (errors) {
      return (
        <ToastHandler errors={{ ...errors }} />
      );
    }

    if (verification.loading) {
      return (
        <div className="center-loader">
          <Loader logo/>
        </div>
      );
    }

    // if (stage === 3) { // status == failed
    //   return (
    //     <ToastHandler errors={{ error: 'You cannot re-verify because your identity check is currently pending' }} />
    //   );
    // }

    if (stage === 2 || stage === 3) { // status == in_progress or status === failed
      const content = this.getVerificationStatusContent();
      return content;
    }

    if (userConsent && (stage === 1 || stage === 4)) { // status == null or status == reupload
      return (
        <div id="onfido-mount" className={`${showCheckFailed && 'onfido-check-failed'}`}>{loading && (
            <div className="center-loader">
              <Loader logo/>
            </div>
          )}
        </div>
      );
    }

    // Terms and Privacy policy User consent page.
    return (
      <div className="onfido-kyc-modal">
        <div className="modal-header">
          <h1>Identity Verification</h1>
          <div className="modal-content">
            For security purposes you must verify your identity through our Onfido integration before continuing to use Vaultoro.
          </div>
          {/* <h5>{`Onfido's privacy statement`} and Terms of Service</h5> */}
          <div className="modal-content">
            By clicking on the &ldquo;Accept&rdquo; button or otherwise continuing to use this service,
             you agree you have read, understand and accept Onfido&apos;s <a href="https://onfido.com/facial-scan-policy-and-release/">Facial Scan Policy & Release</a>,
            <a href="https://onfido.com/privacy/"> Privacy Policy</a> and <a href="https://onfido.com/terms-of-service/">Terms of Service.</a>
          </div>
          <div className="line-separator"/>
        </div>
        <div className="modal-body">
          <h6>FREQUENTLY ASKED QUESTIONS</h6>
          <div className="body-content">
            <div className="faq-head" onClick={() => this.toggleFaq('faq1')}>
              <p>
                What happens to my photographed ID?
              </p>
            </div>
            <ul id="faq1">
              <li>The information contained in the images is examined, including the machine readable data (such as an identity document&apos;s barcode) and the image metadata.</li>
              <li>If you are also asked for your selfie photograph or video, facial scanning technology is used to compare the photo in your government-issued identity document to the image in your selfie photo or video.</li>
              <li>Please see Onfido&apos;s <a href="https://onfido.com/facial-scan-policy-and-release/">Facial Scan Policy & Release</a> and <a href="https://onfido.com/privacy/">Privacy Policy.</a></li>
            </ul>
          </div>
          {/* <div className="line-separator"/> */}
          <div className="body-content">
            <div className="faq-head" onClick={() => this.toggleFaq('faq2')}>
              <p>
                What happens to my selfie photograph or video?
              </p>
            </div>
            <ul id="faq2">
              <li>Facial scanning technology is used to compare the photo in your ID document to the image in your selfie photo or video.</li>
              <li>Please see Onfido&apos;s <a href="https://onfido.com/facial-scan-policy-and-release/">Facial Scan Policy & Release</a> and <a href="https://onfido.com/privacy/">Privacy Policy.</a></li>
            </ul>
          </div>
          {/* <div className="line-separator"/> */}
          <div className="body-content">
            <div className="faq-head" onClick={() => this.toggleFaq('faq3')}>
              <p>
                What happens if I abandon the process after I have clicked &ldquo;Accept&rdquo;?
              </p>
            </div>
            <ul id="faq3">
              <li>If you don&apos;t continue until the end of the verification process, don&apos;t worry. Onfido won&apos;t carry out the facial scan or otherwise complete the check you started.</li>
            </ul>
          </div>
          <div className="line-separator"/>
        </div>
        <div className="modal-footer">
          <div className="">
            <button type="button" className="not-accept-btn" onClick={this.handleNotAccept}>Do not accept</button>
            <button type="button" className="accept-btn" onClick={this.handleAccept}>Accept</button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (store) => ({
  user: store.user,
  loading: store.user.loading,
  verification: store.user.verification,
});

export default connect(mapStateToProps)(OnfidoSdk);
